/**
 * Date/time helpers for calls.
 *
 * A call's `scheduledAt` is a plain ISO-8601 instant with no associated
 * timezone field (unlike calendar events). Calls are a coach-workflow list, so
 * we render times in the viewer's own local timezone — the same behavior the
 * browser gives any `new Date(iso)`. Formatting goes through `Intl.DateTimeFormat`
 * to stay consistent with the rest of the app.
 */

/**
 * How an unscheduled call reads wherever a time would go. A call can be assigned before it has
 * a time, and a blank cell looks like a rendering bug rather than a deliberate state.
 */
export const NO_TIME_LABEL = "No time set";

/** Format a call's scheduled instant as e.g. "Jul 25, 2026, 3:00 PM". */
export const formatCallDateTime = (
  iso: string,
  locale?: string
): string => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

/**
 * Just the wall-clock time of a call in the viewer's zone, e.g. "3:00 PM" — for a calendar
 * chip, which already sits on its day and has no room to repeat the date. Mirrors
 * `formatEventClockTime` for calendar events. Returns "" for a malformed instant rather than
 * throwing, so one bad row never blanks the grid.
 */
export const formatCallClockTime = (iso: string, locale?: string): string => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

/**
 * Convert an ISO instant into the `value` a native
 * `<input type="datetime-local">` expects (`YYYY-MM-DDTHH:mm`), in the viewer's
 * local timezone. Returns "" for empty/invalid input.
 */
export const isoToDateTimeLocalInput = (iso: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Convert a `<input type="datetime-local">` value (local wall-clock, no zone)
 * into an ISO-8601 UTC instant for the API. Returns null when empty/invalid.
 */
export const dateTimeLocalInputToIso = (value: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
};

/**
 * Wall-clock conversion helpers used by the shared `DateTimePicker` live in
 * `src/utils/dateTimeParts.ts`, not here: a shared component must not import from
 * a feature directory. Re-exported so call-related code has one import site.
 */
export type { LocalDateTimeParts } from "../../../../utils/dateTimeParts";
export {
  isoToLocalParts,
  localPartsToIso,
  formatLocalParts,
} from "../../../../utils/dateTimeParts";
